@file:Suppress("MatchingDeclarationName")

package ai.jessie.web.theme

import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.layer
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.breakpoint.BreakpointSizes
import com.varabyte.kobweb.silk.style.layer.SilkLayer
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.cssClass
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.checkbox
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.switch
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.px

/**
 * Font stacks, radii, spacing and sizes from `docs/design/tokens.json`; identical in both modes.
 */
object JessieTokens {
    const val FONT_UI = "\"Instrument Sans\", system-ui, sans-serif"
    const val FONT_BRAND = "\"Instrument Serif\", serif"
    const val FONT_NUM = "\"Dank Mono\", ui-monospace, monospace"

    const val RADIUS_CARD = "12px"
    const val RADIUS_CONTROL = "9px"
    const val RADIUS_CELL = "8px"
    const val RADIUS_PILL = "999px"

    const val PAGE_X = "40px"
    const val PAGE_TOP = "36px"
    const val SECTION_GAP = "28px"
    const val PAGE_X_NARROW = "16px"
    const val PAGE_TOP_NARROW = "20px"
    const val SECTION_GAP_NARROW = "20px"
    const val CARD_PAD = "20px"
    const val SIDE_NAV_WIDTH = "240px"
    const val SLIDE_OVER_WIDTH = "520px"
    const val NAV_HEIGHT = "56px"
    const val CONTROL_HEIGHT = "38px"
    const val CONTROL_HEIGHT_SM = "32px"
    const val INPUT_HEIGHT = "40px"
    const val ROW_HEIGHT = "48px"

    const val SHADOW_SLIDE_OVER = "-16px 0 48px rgba(0, 0, 0, 0.18)"
    const val SHADOW_SEGMENT = "0 1px 2px rgba(0, 0, 0, 0.06)"
}

// Colour tokens as CSS variables (`--j-*`); light values on `:root`, dark under `.silk-dark`.
val GroundVar by StyleVariable<CSSColorValue>(prefix = "j")
val SurfaceVar by StyleVariable<CSSColorValue>(prefix = "j")
val SurfaceMutedVar by StyleVariable<CSSColorValue>(prefix = "j")
val SurfaceSunkenVar by StyleVariable<CSSColorValue>(prefix = "j")
val LineVar by StyleVariable<CSSColorValue>(prefix = "j")
val LineSoftVar by StyleVariable<CSSColorValue>(prefix = "j")
val LineInputVar by StyleVariable<CSSColorValue>(prefix = "j")
val InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val Ink2Var by StyleVariable<CSSColorValue>(prefix = "j")
val Ink3Var by StyleVariable<CSSColorValue>(prefix = "j")
val InkMutedVar by StyleVariable<CSSColorValue>(prefix = "j")
val InkPlaceholderVar by StyleVariable<CSSColorValue>(prefix = "j")
val NavVar by StyleVariable<CSSColorValue>(prefix = "j")
val NavActiveVar by StyleVariable<CSSColorValue>(prefix = "j")
val NavTextVar by StyleVariable<CSSColorValue>(prefix = "j")
val PrimaryVar by StyleVariable<CSSColorValue>(prefix = "j")
val PrimaryHoverVar by StyleVariable<CSSColorValue>(prefix = "j")
val PrimaryTintVar by StyleVariable<CSSColorValue>(prefix = "j")
val PrimaryTintSoftVar by StyleVariable<CSSColorValue>(prefix = "j")
val FocusRingVar by StyleVariable<CSSColorValue>(prefix = "j")
val LinkVar by StyleVariable<CSSColorValue>(prefix = "j")
val LinkHoverVar by StyleVariable<CSSColorValue>(prefix = "j")
val HoursVar by StyleVariable<CSSColorValue>(prefix = "j")
val HoursTintVar by StyleVariable<CSSColorValue>(prefix = "j")
val DangerVar by StyleVariable<CSSColorValue>(prefix = "j")
val DangerTintVar by StyleVariable<CSSColorValue>(prefix = "j")
val WarningVar by StyleVariable<CSSColorValue>(prefix = "j")
val WarningTintVar by StyleVariable<CSSColorValue>(prefix = "j")
val AccentVar by StyleVariable<CSSColorValue>(prefix = "j")
val AccentTintVar by StyleVariable<CSSColorValue>(prefix = "j")
val Heat1Var by StyleVariable<CSSColorValue>(prefix = "j")
val Heat1InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val Heat2Var by StyleVariable<CSSColorValue>(prefix = "j")
val Heat2InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val Heat3Var by StyleVariable<CSSColorValue>(prefix = "j")
val Heat3InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val Heat4Var by StyleVariable<CSSColorValue>(prefix = "j")
val Heat4InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val Heat5Var by StyleVariable<CSSColorValue>(prefix = "j")
val Heat5InkVar by StyleVariable<CSSColorValue>(prefix = "j")
val OverlayVar by StyleVariable<CSSColorValue>(prefix = "j")
val NumWeightVar by StyleVariable<Int>(prefix = "j")

/**
 * Raw CSS declarations; keeps the values identical to the design tokens and the former stylesheet.
 */
internal fun css(vararg declarations: Pair<String, Any>): Modifier = Modifier.styleModifier {
    declarations.forEach { (name, value) -> property(name, value.toString()) }
}

private fun hex(value: Int): Color.Rgb = Color.rgb(value)

private const val LIGHT_GROUND = 0xF4F3EF
private const val LIGHT_INK = 0x17191F
private const val DARK_GROUND = 0x15171C
private const val DARK_INK = 0xECEDF0

@Suppress(
    "MagicNumber"
) // hex values are the tokens themselves (docs/design/tokens.json → color.light)
private fun lightTokens(): Modifier =
    Modifier.setVariable(GroundVar, hex(LIGHT_GROUND))
        .setVariable(SurfaceVar, hex(0xFFFFFF))
        .setVariable(SurfaceMutedVar, hex(0xFAF9F6))
        .setVariable(SurfaceSunkenVar, hex(0xEEEDE8))
        .setVariable(LineVar, hex(0xE4E1DA))
        .setVariable(LineSoftVar, hex(0xEEEDE8))
        .setVariable(LineInputVar, hex(0xD9D6CF))
        .setVariable(InkVar, hex(LIGHT_INK))
        .setVariable(Ink2Var, hex(0x3D4250))
        .setVariable(Ink3Var, hex(0x5B6070))
        .setVariable(InkMutedVar, hex(0x7A7F8C))
        .setVariable(InkPlaceholderVar, hex(0x9AA0AE))
        .setVariable(NavVar, hex(0x171B26))
        .setVariable(NavActiveVar, hex(0x2A3040))
        .setVariable(NavTextVar, hex(0xB4B9C7))
        .setVariable(PrimaryVar, hex(0x2B4FD1))
        .setVariable(PrimaryHoverVar, hex(0x1E3CA8))
        .setVariable(PrimaryTintVar, hex(0xE6EBFA))
        .setVariable(PrimaryTintSoftVar, hex(0xEEF1FB))
        .setVariable(FocusRingVar, hex(0xDDE4FA))
        .setVariable(LinkVar, PrimaryVar.value())
        .setVariable(LinkHoverVar, PrimaryHoverVar.value())
        .setVariable(HoursVar, hex(0x1F8A5B))
        .setVariable(HoursTintVar, hex(0xE3F3EB))
        .setVariable(DangerVar, hex(0xB4321F))
        .setVariable(DangerTintVar, hex(0xFBE9E7))
        .setVariable(WarningVar, hex(0x8A5A00))
        .setVariable(WarningTintVar, hex(0xFFF4D6))
        .setVariable(AccentVar, hex(0x6B3FB5))
        .setVariable(AccentTintVar, hex(0xF0E8FA))
        .setVariable(Heat1Var, hex(0xE3F3EB))
        .setVariable(Heat1InkVar, hex(0x1F8A5B))
        .setVariable(Heat2Var, hex(0xBFE5D1))
        .setVariable(Heat2InkVar, hex(0x14603F))
        .setVariable(Heat3Var, hex(0x7FCBA3))
        .setVariable(Heat3InkVar, hex(0x0F4A31))
        .setVariable(Heat4Var, hex(0x3AAE78))
        .setVariable(Heat4InkVar, hex(0xFFFFFF))
        .setVariable(Heat5Var, hex(0x1F8A5B))
        .setVariable(Heat5InkVar, hex(0xFFFFFF))
        .setVariable(OverlayVar, Color.rgba(23, 25, 31, 0.38f))
        .setVariable(NumWeightVar, 400)

@Suppress(
    "MagicNumber"
) // hex values are the tokens themselves (docs/design/tokens.json → color.dark)
private fun darkTokens(): Modifier =
    Modifier.setVariable(GroundVar, hex(DARK_GROUND))
        .setVariable(SurfaceVar, hex(0x1E2128))
        .setVariable(SurfaceMutedVar, hex(0x23262E))
        .setVariable(SurfaceSunkenVar, hex(0x2C303A))
        .setVariable(LineVar, hex(0x2C303A))
        .setVariable(LineSoftVar, hex(0x2C303A))
        .setVariable(LineInputVar, hex(0x363A45))
        .setVariable(InkVar, hex(DARK_INK))
        .setVariable(Ink2Var, hex(0xC5C9D3))
        .setVariable(Ink3Var, hex(0xA3A8B5))
        .setVariable(InkMutedVar, hex(0x8B90A0))
        .setVariable(InkPlaceholderVar, hex(0x6C7180))
        .setVariable(NavVar, hex(0x0F1116))
        .setVariable(NavActiveVar, hex(0x23262E))
        .setVariable(NavTextVar, hex(0xA3A8B5))
        .setVariable(PrimaryVar, hex(0x5B79F0))
        .setVariable(PrimaryHoverVar, hex(0x8FA4FF))
        .setVariable(PrimaryTintVar, hex(0x26304F))
        .setVariable(PrimaryTintSoftVar, hex(0x26304F))
        .setVariable(FocusRingVar, hex(0x26304F))
        .setVariable(LinkVar, hex(0x8FA4FF))
        .setVariable(LinkHoverVar, hex(0xB3C2FF))
        .setVariable(HoursVar, hex(0x3FBF83))
        .setVariable(HoursTintVar, hex(0x1B3A2C))
        .setVariable(DangerVar, hex(0xF09A8C))
        .setVariable(DangerTintVar, hex(0x3B2320))
        .setVariable(WarningVar, hex(0xF1C46B))
        .setVariable(WarningTintVar, hex(0x3A2F14))
        .setVariable(AccentVar, hex(0xC9AEF5))
        .setVariable(AccentTintVar, hex(0x33264A))
        .setVariable(Heat1Var, hex(0x1B3A2C))
        .setVariable(Heat1InkVar, hex(0x6FD8A5))
        .setVariable(Heat2Var, hex(0x1F5A3F))
        .setVariable(Heat2InkVar, hex(0x9FE8C4))
        .setVariable(Heat3Var, hex(0x257A54))
        .setVariable(Heat3InkVar, hex(0xE3F3EB))
        .setVariable(Heat4Var, hex(0x2E9E6B))
        .setVariable(Heat4InkVar, hex(0xFFFFFF))
        .setVariable(Heat5Var, hex(0x3FBF83))
        .setVariable(Heat5InkVar, hex(0x0B2A1C))
        .setVariable(OverlayVar, Color.rgba(0, 0, 0, 0.55f))
        .setVariable(NumWeightVar, 500)

private const val NARROW_BELOW_PX = 901

@Suppress("MagicNumber") // Silk default breakpoints in px; only `lg` follows the design (≤ 900 px).
private fun breakpoints() =
    BreakpointSizes(sm = 480.px, md = 768.px, lg = NARROW_BELOW_PX.px, xl = 1280.px, xxl = 1536.px)

// Silk's Checkbox and Switch take concrete palette colours: primary, primaryHover, lineInput.
@Suppress("MagicNumber")
private fun widgetPalettes(ctx: InitSilkContext) {
    val white = hex(0xFFFFFF)
    ctx.theme.palettes.light.checkbox.set(hex(0x2B4FD1), hex(0x1E3CA8), white)
    ctx.theme.palettes.dark.checkbox.set(hex(0x5B79F0), hex(0x8FA4FF), white)
    ctx.theme.palettes.light.switch.set(hex(0x2B4FD1), hex(0xD9D6CF), white)
    ctx.theme.palettes.dark.switch.set(hex(0x5B79F0), hex(0x363A45), white)
}

/**
 * Design v2 tokens and base element rules. The Dank Mono `@font-face` lives in the index head
 * (`web/build.gradle.kts`): Silk serialises rules through an element style, which drops `src`.
 */
@InitSilk
fun initJessieTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = hex(LIGHT_GROUND)
    ctx.theme.palettes.light.color = hex(LIGHT_INK)
    ctx.theme.palettes.dark.background = hex(DARK_GROUND)
    ctx.theme.palettes.dark.color = hex(DARK_INK)
    ctx.theme.breakpoints = breakpoints()
    widgetPalettes(ctx)

    ctx.stylesheet.registerStyleBase(":root") { lightTokens() }
    ctx.stylesheet.registerStyleBase(".${ColorMode.DARK.cssClass}") { darkTokens() }

    // Layered, so that component styles (higher Silk layers) win over element defaults.
    ctx.stylesheet.layer(SilkLayer.BASE) {
        registerStyleBase("html") { css("background" to GroundVar.value()) }
        registerStyleBase("body") {
            css(
                "margin" to "0",
                "font-family" to JessieTokens.FONT_UI,
                "font-size" to "14px",
                "line-height" to "1.5",
                "color" to InkVar.value(),
                "background" to GroundVar.value(),
                "-webkit-font-smoothing" to "antialiased",
            )
        }
        registerStyleBase("a") { css("color" to LinkVar.value(), "text-decoration" to "none") }
        registerStyleBase("a:hover") { css("color" to LinkHoverVar.value()) }
        registerStyleBase("h1, h2, h3") { css("margin" to "0", "letter-spacing" to "-0.01em") }
        registerStyleBase("h1") {
            css("font-size" to "32px", "font-weight" to "700", "letter-spacing" to "-0.02em")
        }
        registerStyleBase("h2") { css("font-size" to "20px", "font-weight" to "600") }
        registerStyleBase("h3") { css("font-size" to "17px", "font-weight" to "600") }
        registerStyleBase("button, input, select, textarea") {
            css("font" to "inherit", "color" to "inherit")
        }
        registerStyleBase(":focus-visible") {
            css("outline" to "2px solid ${PrimaryVar.value()}", "outline-offset" to "2px")
        }
        // Titles and alerts take focus by script only to announce a new view; no control ring.
        // Radio-group fieldsets take focus on a problem and keep the ring.
        registerStyleBase("[tabindex=\"-1\"]:not(fieldset):focus") { css("outline" to "none") }
    }
}
